import { create } from "zustand";
import { DEFAULT_MASTER_DATA, type CalcContext, type EquipmentTemplate, type MasterData, type MethodConfig, type Stage, type Technology } from "@h2lca/core";
import { api } from "@/lib/api";

export type MasterOrigin = "server" | "bundled";

interface MasterState {
  data: MasterData;
  origin: MasterOrigin;
  /** 서버가 종류별로 DB 값을 썼는지 내장값을 썼는지 */
  serverOrigin: Record<string, "db" | "default"> | null;
  loadedAt: string | null;
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
}

/**
 * 마스터 데이터(배출계수,설비,예시,용어,산정방식).
 * 서버(/api/master)가 정본이고, 서버가 안 되면 패키지에 내장된 기본값으로 돈다 , 계산이 멈추면 안 된다.
 */
export const useMaster = create<MasterState>((set) => ({
  data: DEFAULT_MASTER_DATA,
  origin: "bundled",
  serverOrigin: null,
  loadedAt: null,
  loading: false,
  error: null,
  async load() {
    set({ loading: true, error: null });
    try {
      const r = await api<{ data: MasterData; origin: Record<string, "db" | "default">; loadedAt: string }>("/master");
      set({ data: r.data, origin: "server", serverOrigin: r.origin, loadedAt: r.loadedAt, loading: false });
    } catch (e) {
      set({ origin: "bundled", loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },
}));

/* ── 선택자(파생값) ─────────────────────────────────────────── */

export const selectCtx = (s: MasterState): CalcContext => ({ factors: s.data.factors, sources: s.data.sources, methods: s.data.methods });

export function methodById(data: MasterData, id: string): MethodConfig {
  const m = data.methods.find((x) => x.id === id);
  if (!m) throw new Error(`method not found: ${id}`);
  return m;
}

export function technologiesFor(data: MasterData, stage: Stage): Technology[] {
  return data.technologies.filter((t) => t.active && t.stage === stage).sort((a, b) => a.order - b.order);
}

/** 팔레트: 분류,기술로 거른 템플릿. 기술과 무관한 공통 설비(techId 없음)는 항상 함께 보인다 */
export function templatesFor(data: MasterData, stage: Stage | "all", techId?: string): EquipmentTemplate[] {
  return data.templates
    .filter((t) => t.active)
    .filter((t) => stage === "all" || t.stage === stage)
    .filter((t) => !techId || !t.techId || t.techId === techId)
    .sort((a, b) => a.order - b.order);
}

export function templateById(data: MasterData, id: string): EquipmentTemplate | undefined {
  return data.templates.find((t) => t.id === id);
}

export function glossary(data: MasterData, key: string) {
  return data.glossary.find((g) => g.key === key);
}
